/**
 * Voxly — Premium emoji renderer.
 *
 * Replaces the OS's native (often pixelated / inconsistent) emoji rendering
 * with Twemoji SVG assets served from jsDelivr's CDN. Same look on every
 * device, crisp at any size, ~2 KB per SVG.
 *
 * Falls back gracefully to the native emoji glyph (still readable) if the
 * SVG fails to load.
 */
import React, { useState } from "react";

const TWEMOJI_CDN = "https://cdn.jsdelivr.net/gh/jdecked/twemoji@15.1.0/assets/svg";

/** Convert an emoji string → twemoji file basename (hex codepoints joined by `-`).
 *  We strip the FE0F variation selector (Apple's "emoji presentation" hint)
 *  because Twemoji files are named without it. */
function emojiToCodepoint(str) {
  return Array.from(str)
    .map((c) => c.codePointAt(0).toString(16))
    .filter((cp) => cp !== "fe0f")
    .join("-");
}

export function twemojiUrl(emoji) {
  return `${TWEMOJI_CDN}/${emojiToCodepoint(emoji)}.svg`;
}

/**
 * <Emoji char="❤️" size={28} />
 *
 * Renders the Twemoji SVG inline. On failure, falls back to the native glyph.
 */
export default function Emoji({ char, size = 24, className = "", style = {} }) {
  const [failed, setFailed] = useState(false);

  if (failed || !char) {
    return (
      <span
        className={className}
        style={{ fontSize: `${size}px`, lineHeight: 1, ...style }}
        aria-label={char}
      >
        {char}
      </span>
    );
  }

  return (
    <img
      src={twemojiUrl(char)}
      alt={char}
      width={size}
      height={size}
      draggable={false}
      onError={() => setFailed(true)}
      className={`inline-block select-none ${className}`}
      style={{ width: size, height: size, ...style }}
    />
  );
}
